<template>
  <div id="workPermit" class="border-none xl:border-solid xl:p-8" style="border: 1px solid #cdcdcd">
    <!-- title -->
    <h5 class="hidden xl:flex mb-4 align-center gap-2">
      <i class="dot"></i> 작업허가
      <span style="font-size: 14px; font-weight: 400; color: #696d71">※ 최초허가기간(최대 9시간) 종료후 최대 4시간까지 연장허가 가능</span>
    </h5>

    <!-- content -->
    <div v-if="작업허가.작업시간">
      <div class="writeGridContainer mb-0">
        <!-- 시작 -->
        <div class="writeGridLineItem order-1">
          <div class="font-medium">작업시작일시</div>
          <div class="flex flex-wrap items-center gap-4">
            <date-selector
              ref="작업시작일시"
              class="w-full xl:w-auto"
              v-model="작업허가.작업시작일시"
              :readonly="readonly"
              @input="handleChangeDate"
              @focusin="focusinDay"
              @focusout="focusoutDay"
              :debug="true"
            />
            <time-selector
              class="w-full xl:w-auto"
              v-model="작업허가.작업시간.시작"
              :readonly="readonly"
              :enableSeconds="false"
              @focusin="focusinDay"
              @focusout="focusoutDay"
            />
          </div>
        </div>

        <!-- 종료 -->
        <div class="writeGridLineItem order-2">
          <div class="font-medium">작업종료일시</div>
          <div class="flex flex-wrap items-center justify-end xl:justify-start gap-4">
            <date-selector
              ref="작업종료일시"
              class="w-full xl:w-auto"
              v-model="작업허가.작업종료일시"
              :readonly="readonly"
              @input="handleChangeDate"
              @focusin="focusinDay"
              @focusout="focusoutDay"
            />
            <time-selector
              class="w-full xl:w-auto"
              v-model="작업허가.작업시간.종료"
              :readonly="readonly"
              :enableSeconds="false"
              @focusin="focusinDay"
              @focusout="focusoutDay"
            />
            <div v-if="useExtendedTime && 작업허가.작업승인.상태 === '승인' && 작업허가.작업종료.상태 === '대기' && 작업허가.연장승인.상태 === '없음'">
              <vs-button
                color="secondary"
                @click="
                  () => {
                    작업허가.연장승인.상태 = '대기';
                    작업허가.연장종료일시 = 작업허가.작업종료일시;
                    작업허가.작업시간.연장 = 작업허가.작업시간.종료;
                    onChangeDate();
                  }
                ">연장신청</vs-button>
            </div>
          </div>
        </div>

        <!-- 연장 -->
        <div v-if="작업허가.연장승인.상태 !== '없음'" class="writeGridLineItem order-3">
          <div class="font-medium">연장종료일시</div>
          <div class="flex flex-wrap items-center justify-end xl:justify-start gap-4">
            <date-selector class="w-full xl:w-auto" v-model="작업허가.연장종료일시" :readonly="readonly" :minDate="extendedMinDay.date" :maxDate="extendedMaxDay.date" @input="onChangeDate" />
            <time-selector class="w-full xl:w-auto" v-model="작업허가.작업시간.연장" :readonly="readonly" :enableSeconds="false" :minTime="extendedMinDay.time" :maxTime="extendedMaxDay.time" @focusout="onChangeDate" />

            <!-- 업체명에 포함된 인원만 연장할 수 있음 -->
            <div v-if="useExtendedTime && 작업허가.작업승인.상태 === '승인' && 작업허가.작업종료.상태 === '대기' && 작업허가.연장승인.상태 !== '없음' && 작업허가.연장승인.상태 !== '승인'">
              <vs-button
                color="#DCDFE2"
                style="color: #3c3c3c"
                @click="
                  작업허가.연장승인.상태 = '없음';
                  작업허가.작업시간.연장 = '';
                ">연장취소</vs-button>
            </div>
          </div>
        </div>

        <!-- 작업승인 -->
        <work-permit-option
          v-if="useApproval"
          class="order-4"
          title="작업승인"
          v-model="작업허가.작업승인"
          :readonly-radio="readonly || 작업허가.연장승인.상태 !== '없음' || 작업허가.작업종료.상태 !== '대기'"
          :is-safety-manager="isSafetyManager"
          @select="(status) => onSelect('작업승인', status)"
        />

        <!-- 연장승인 -->
        <work-permit-option
          v-if="useExtendedTime && 작업허가.연장승인.상태 !== '없음' && isSafetyManager"
          class="order-5"
          title="연장승인"
          v-model="작업허가.연장승인"
          :readonly-radio="readonly || 작업허가.작업종료.상태 !== '대기'"
          :is-safety-manager="isSafetyManager"
          @select="(status) => onSelect('연장승인', status)"
        />

        <!-- 작업종료 -->
        <work-permit-option v-if="(!useApproval || (작업허가.작업승인.상태 === '승인' && (작업허가.연장승인.상태 === '없음' || 작업허가.연장승인.상태 === '승인'))) && isSafetyManager"
                            class="order-6" title="작업종료" v-model="작업허가.작업종료" :is-safety-manager="isSafetyManager">
          <template v-if="isSafetyManager">
            <vs-button v-if="작업허가.작업종료.상태 === '대기'" class="w-full xl:w-auto" color="black" @click="onSelect('작업종료', '승인')">작업완료</vs-button>
            <vs-button v-else class="w-full xl:w-auto" color="#DCDFE2" style="color: #3c3c3c" @click="onSelect('작업종료', '대기')">완료취소</vs-button>
          </template>
        </work-permit-option>

      </div>
    </div>
  </div>
</template>

<script>
import DateSelector from "@/components/selector/DateSelector.vue";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import WorkPermitOption from "@/components/work-permit/WorkPermitOption.vue";

export default {
  name: "WorkPermit",
  components: { WorkPermitOption, RadioGroup, TimeSelector, DateSelector },
  props: {
    readonly: Boolean,
    isPrint: Boolean,
    board: Object,
    value: Object,

    // 작업승인, 연장승인, 작업종료 버튼을 클릭했을 때 실행할 함수 (유효성 검사)
    funcSelectOption: Function,

    // 승인 절차
    useApproval: {
      type: Boolean,
      default: true,
    },

    // 연장 절차
    useExtendedTime: {
      type: Boolean,
      default: true,
    },
    // 연장신청, 연장취소 버튼을 표시할 회사 id
    // 사용자의 회사 id와 일치할 경우 버튼을 표시
    companyId: Number,
    // TBM 작성상태: 미진행, 진행중, 완료
    tbmStatus: String,
    // 안전관리자 권한을 갖고있는지
    isSafetyManager: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      작업허가: this.value,
      oldDate: {
        작업시작일시: '',
        작업종료일시: '',
        작업시간: {
          시작: '',
          종료: '',
        },
      }
    };
  },
  computed: {
    myInfo() {
      return {
        company_id: 0, // userInfo 를 가져오기전 임의로 사용하기 위해 설정
        ...this.$store.state.auth.userInfo,
      };
    },
    endMinDay() {
      if (!(this.작업허가.작업시작일시 && this.작업허가.작업시간.시작)) {
        return {
          date: undefined,
          time: "00:00",
          hour: "00",
          minute: "00",
        };
      }

      const oldDate = new Date(`${this.작업허가.작업시작일시} ${this.작업허가.작업시간.시작}`);
      let time = "00:00";

      if (this.작업허가.작업종료일시 && new Date(this.작업허가.작업종료일시).getDay() === oldDate.getDay()) {
        time = oldDate.format("HH:mm");
      }

      return {
        date: oldDate.format("yyyy-MM-dd"),
        time,
        hour: time.split(":")[0],
        minute: time.split(":")[1],
      };
    },
    endMaxDay() {
      if (!(this.작업허가.작업시작일시 && this.작업허가.작업시간.시작)) {
        return {
          date: undefined,
          time: "00:00",
          hour: "00",
          minute: "00",
        };
      }

      // 작업시작일시 + 9시간 이전까지 선택 가능
      const oldDate = new Date(`${new Date(this.작업허가.작업시작일시).format('yyyy-MM-dd')  } ${  this.작업허가.작업시간.시작}`);
      const newDate = new Date(new Date(oldDate).setHours(oldDate.getHours() + 9));
      let time = "23:59";

      if (this.작업허가.작업종료일시 && new Date(this.작업허가.작업종료일시).getDay() === newDate.getDay()) {
        time = newDate.format("HH:mm");
      }

      return {
        date: newDate.format("yyyy-MM-dd"),
        time,
        hour: time.split(":")[0],
        minute: time.split(":")[1],
      };
    },
    extendedMinDay() {
      if (!(this.작업허가.작업종료일시 && this.작업허가.작업시간.종료)) {
        return {
          date: undefined,
          time: "00:00",
          hour: "00",
          minute: "00",
        };
      }

      const oldDate = new Date(`${this.작업허가.작업종료일시} ${this.작업허가.작업시간.종료}`);
      let time = "00:00";

      if (this.작업허가.연장종료일시 && new Date(this.작업허가.연장종료일시).getDay() === oldDate.getDay()) {
        time = oldDate.format("HH:mm");
      }

      return {
        date: oldDate.format("yyyy-MM-dd"),
        time,
        hour: time.split(":")[0],
        minute: time.split(":")[1],
      };
    },
    extendedMaxDay() {
      if (!(this.작업허가.작업종료일시 && this.작업허가.작업시간.종료)) {
        return {
          date: undefined,
          time: "00:00",
          hour: "00",
          minute: "00",
        };
      }

      // 작업종료일시 + 4시간 이전까지 선택 가능
      const oldDate = new Date(`${this.작업허가.작업종료일시} ${this.작업허가.작업시간.종료}`);
      const newDate = new Date(new Date(oldDate).setHours(oldDate.getHours() + 4));
      let time = "00:00";

      if (this.작업허가.연장종료일시 && new Date(this.작업허가.연장종료일시).getDay() === newDate.getDay()) {
        time = newDate.format("HH:mm");
      }

      return {
        date: newDate.format("yyyy-MM-dd"),
        time,
        hour: time.split(":")[0],
        minute: time.split(":")[1],
      };
    },
  },
  watch: {
    value(data) {
      if (!data || Object.keys(data).length === 0) {
        this.initData();
      } else {
        // 기존 승인자 정보 백업
        const backupInfo = {};

        // 작업승인 정보 백업
        if (this.작업허가.작업승인 && this.작업허가.작업승인.승인자) {
          if (!backupInfo.작업승인) backupInfo.작업승인 = {};
          backupInfo.작업승인.승인자 = JSON.parse(JSON.stringify(this.작업허가.작업승인.승인자));
          backupInfo.작업승인.승인일시 = this.작업허가.작업승인.승인일시;
        }

        // 연장승인 정보 백업
        if (this.작업허가.연장승인 && this.작업허가.연장승인.승인자) {
          if (!backupInfo.연장승인) backupInfo.연장승인 = {};
          backupInfo.연장승인.승인자 = JSON.parse(JSON.stringify(this.작업허가.연장승인.승인자));
          backupInfo.연장승인.승인일시 = this.작업허가.연장승인.승인일시;
        }

        // 작업종료 정보 백업
        if (this.작업허가.작업종료 && this.작업허가.작업종료.승인자) {
          if (!backupInfo.작업종료) backupInfo.작업종료 = {};
          backupInfo.작업종료.승인자 = JSON.parse(JSON.stringify(this.작업허가.작업종료.승인자));
          backupInfo.작업종료.승인일시 = this.작업허가.작업종료.승인일시;
        }

        // 데이터 업데이트
        this.작업허가 = data;

        // 백업한 정보 복원 (필요한 경우)
        if (backupInfo.작업승인 && backupInfo.작업승인.승인일시 && backupInfo.작업승인.승인자) {
          if (!this.작업허가.작업승인 || !this.작업허가.작업승인.승인자 ||
            Object.keys(this.작업허가.작업승인.승인자).length === 0) {
            if (!this.작업허가.작업승인) this.작업허가.작업승인 = {};
            this.작업허가.작업승인.승인자 = backupInfo.작업승인.승인자;
          }
        }

        if (backupInfo.연장승인 && backupInfo.연장승인.승인일시 && backupInfo.연장승인.승인자) {
          if (!this.작업허가.연장승인 || !this.작업허가.연장승인.승인자 ||
            Object.keys(this.작업허가.연장승인.승인자).length === 0) {
            if (!this.작업허가.연장승인) this.작업허가.연장승인 = {};
            this.작업허가.연장승인.승인자 = backupInfo.연장승인.승인자;
          }
        }

        if (backupInfo.작업종료 && backupInfo.작업종료.승인일시 && backupInfo.작업종료.승인자) {
          if (!this.작업허가.작업종료 || !this.작업허가.작업종료.승인자 ||
            Object.keys(this.작업허가.작업종료.승인자).length === 0) {
            if (!this.작업허가.작업종료) this.작업허가.작업종료 = {};
            this.작업허가.작업종료.승인자 = backupInfo.작업종료.승인자;
          }
        }
      }
    },
    작업허가: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  created() {
    if (!this.value || Object.keys(this.value).length === 0) {
      this.initData();
    }
  },
  methods: {
    initData() {
      this.작업허가 = {
        작업시작일시: "",
        작업종료일시: "",
        연장종료일시: "",
        작업시간: {
          시작: "",
          종료: "",
          연장: "",
          error: "",
        },
        작업승인: {
          상태: "대기",
          승인자: {
            부서: "",
            소속: "",
            직책: "",
            성명: "",
            서명: { sign: "" },
          },
          승인일시: "",
          error: "",
        },
        연장승인: {
          상태: "없음",
          승인자: {
            부서: "",
            소속: "",
            직책: "",
            성명: "",
            서명: { sign: "" },
          },
          승인일시: "",
          error: "",
        },
        작업종료: {
          상태: "대기",
          승인자: {
            부서: "",
            소속: "",
            직책: "",
            성명: "",
            서명: { sign: "" },
          },
          승인일시: "",
          error: "",
        },
      };
    },

    onSelect(type, status) {
      if (this.funcSelectOption) {
        const result = this.funcSelectOption(type, status);

        if ((typeof result === "boolean" && !result) || (typeof result === "object" && result.error)) {
          this.$nextTick(() => {
            this.작업허가[type] = {
              ...this.작업허가[type],
              상태: "대기",
              error: result.error,
            };
          });
          return;
        } else {
          this.작업허가[type].error = "";
        }
      }

      const 부서 = this.board.writer.section || this.myInfo.section || this.board.writer.department || this.myInfo.department || '-';
      const 소속 = this.board.writer.department || this.myInfo.department || this.board.writer.section || this.myInfo.section || '-';
      const 직책 = this.board.writer.rank || this.myInfo.rank || '-';
      const 성명 = this.board.writer.name || this.myInfo.name || '-';
      const 서명 = this.board.writer.서명 || this.myInfo.서명 || { sign: "" };

      if (type === "작업승인") {
        if (status === "승인" || status === "반려") {
          this.작업허가.작업승인.승인자 = { 부서, 소속, 직책, 성명, 서명 };
          this.작업허가.작업승인.승인일시 = new Date().format("yyyy-MM-dd HH:mm");

          if (status === "승인") {
            // 작업승인의 상태가 '승인'일 경우 작업시간의 시작시간을 작업승인일시로 변경
            this.작업허가.작업시간.시작 = new Date(this.작업허가.작업승인.승인일시).format("HH:mm");
            this.onChangeDate();
          }
        }
      }

      if (type === "연장승인") {
        if (status === "승인" || status === "반려") {
          this.작업허가.연장승인.승인자 = { 부서, 소속, 직책, 성명, 서명 };
          this.작업허가.연장승인.승인일시 = new Date().format("yyyy-MM-dd HH:mm");
        }
      }

      if (type === "작업종료") {
        if (status === "대기") {
          this.작업허가.작업종료.상태 = "대기";
          this.작업허가.작업종료.승인자 = {
            부서: "",
            소속: "",
            직책: "",
            성명: "",
          };
        }

        if (status === "승인") {
          this.작업허가.작업종료.상태 = "승인";
          this.작업허가.작업종료.승인자 = { 부서, 소속, 직책, 성명, 서명 };
          this.작업허가.작업종료.승인일시 = new Date().format("yyyy-MM-dd HH:mm");

          // 작업종료의 상태가 '승인'일 경우 작업시간의 종료시간을 작업종료일시로 변경

          // 작업종료 작업종료.승인일시가 최대허용시간(작업시작일시 + 9시간) 이 지난 경우,
          // 작업종료일시가 최대허용시간으로 변경
          const oldDate = new Date(`${new Date(this.작업허가.작업시작일시).format('yyyy-MM-dd')  } ${  this.작업허가.작업시간.시작}`);
          const 최대허용일시 = new Date(oldDate.setHours(oldDate.getHours() + 9));

          if (new Date(this.작업허가.작업종료.승인일시) > 최대허용일시) {
            this.작업허가.작업종료일시 = new Date(최대허용일시).format("yyyy-MM-dd");
            this.작업허가.작업시간.종료 = new Date(최대허용일시).format("HH:mm");
          } else {
            // 작업종료일시가 최대허용일시 이내인 경우
            this.작업허가.작업종료일시 = new Date(this.작업허가.작업종료.승인일시).format("yyyy-MM-dd");
            this.작업허가.작업시간.종료 = new Date(this.작업허가.작업종료.승인일시).format("HH:mm");
          }

        }
      }
    },

    async handleChangeDate() {
      await this.focusoutDay();
      await this.$nextTick();
      this.onChangeDate();
    },
    // 시간변경 을 watch 에 등록시 호출이 잦을 것으로 예상되어 input 이벤트로 변경
    onChangeDate() {
      this.$emit("changeDate");
    },
    focusinDay () {
      // 현재 작업일시를 oldDate 에 저장
      this.oldDate.작업시작일시 = this.작업허가.작업시작일시;
      this.oldDate.작업종료일시 = this.작업허가.작업종료일시;
      this.oldDate.작업시간.시작 = this.작업허가.작업시간.시작;
      this.oldDate.작업시간.종료 = this.작업허가.작업시간.종료;
    },
    async focusoutDay () {
      await this.$nextTick();
      if (this.작업허가.작업시작일시 && this.작업허가.작업시간.시작 && this.작업허가.작업종료일시 && this.작업허가.작업시간.종료) {
        let revert = false;

        // 시작일 + 시작시간
        const startDateTime = new Date(`${this.작업허가.작업시작일시}T${this.작업허가.작업시간.시작}`);
        // 종료일 + 종료시간
        const endDateTime = new Date(`${this.작업허가.작업종료일시}T${this.작업허가.작업시간.종료}`);

        // 종료일이 시작일보다 이전인 경우
        if (endDateTime < startDateTime) {
          revert = true;
          // 작업종료일시가 작업시작일시보다 이전인 경우 팝업을 띄우고, 이전상태로 돌아간다
          this.$vs.notify({
            title: "오류",
            text: "작업종료일시는 작업시작일시 이후로 설정해야 합니다.",
            color: "danger",
            icon: "close",
            position: 'bottom-center',
            duration: 5000,
          });
        }

        const diffMs = endDateTime - startDateTime;
        const diffHours = diffMs / (1000 * 60 * 60);
        if (!revert && diffHours > 9) {
          revert = true;
          // 작업종료시간이 작업시작시간 + 9시간을 초과하는 경우 팝업을 띄우고, 이전상태로 돌아간다
          this.$vs.notify({
            title: "오류",
            text: "작업시간은 9시간 이내로 설정해야 합니다.",
            color: "danger",
            icon: "close",
            position: 'bottom-center',
            duration: 5000,
          });
        }

        if (revert) {
          this.작업허가.작업시작일시 = this.oldDate.작업시작일시
          this.작업허가.작업종료일시 = this.oldDate.작업종료일시
          this.작업허가.작업시간.시작 = this.oldDate.작업시간.시작;
          this.작업허가.작업시간.종료 = this.oldDate.작업시간.종료;
        }
      }
    },

    getDate(date, defaultDate) {
      try {
        if (date) {
          return new Date(date).format("yyyy-MM-dd");
        }
      } catch (e) {
        console.error('날짜 변환 오류:', e);
      }
      return defaultDate;
    },
    forceRefresh() {
      // 컴포넌트 내 date selector 강제 리렌더링
      this.$nextTick(() => {
        // 작업허가 객체를 복사하여 반응성 트리거
        const backup = JSON.parse(JSON.stringify(this.작업허가));
        this.작업허가 = { ...backup };

        // DOM이 업데이트된 후 date selector 컴포넌트 초기화 강제
        this.$nextTick(() => {
          const dateSelectors = this.$el.querySelectorAll('.vc-date-picker');
          dateSelectors.forEach(selector => {
            if (selector.__vue__) {
              selector.__vue__.$forceUpdate();

              // 내부 datePicker ref에 접근하여 강제 업데이트
              if (selector.__vue__.$refs && selector.__vue__.$refs.datePicker) {
                selector.__vue__.$refs.datePicker.$forceUpdate();
              }
            }
          });
        });
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.align-center {
  align-items: center;
}

.success-color {
  color: #28c76f;
}
.alert-color {
  color: #ea5455;
}
</style>
